import { z } from 'zod';

// Canonical, model-facing product vocabularies. These are the FINAL names the
// hardened snapshot KB will keep; only the data adapter behind PRODUCT_CATALOG_PORT
// changes when the preview is replaced.
export const PRODUCT_CONCERNS = [
  'hydration',
  'acne',
  'redness-sensitivity',
  'hyperpigmentation',
  'pores',
  'aging-wrinkles',
  'dullness',
  'dark-circles',
  'barrier',
  'oil-control',
] as const;
export const ProductConcernSchema = z.enum(PRODUCT_CONCERNS);
export type ProductConcern = z.infer<typeof ProductConcernSchema>;

export const PRODUCT_SKIN_TYPES = ['dry', 'oily', 'combination', 'normal', 'sensitive'] as const;
export const ProductSkinTypeSchema = z.enum(PRODUCT_SKIN_TYPES);
export type ProductSkinType = z.infer<typeof ProductSkinTypeSchema>;

// --- Provenance --------------------------------------------------------------

// A union from the beginning: the live API adapter returns live_api; the hardened
// adapter will return snapshot. This prevents a live read from claiming
// cross-call consistency it does not have.
export const ProductDataVersionSchema = z.union([
  z.object({ kind: z.literal('live_api'), queriedAt: z.iso.datetime() }),
  z.object({ kind: z.literal('snapshot'), snapshotId: z.uuid() }),
]);
export type ProductDataVersion = z.infer<typeof ProductDataVersionSchema>;

export const PRODUCT_FACT_SOURCES = [
  'catalog',
  'member_aggregate',
  'survey_mapping',
  'brand_partner',
  'deterministic_inci',
  'unknown',
] as const;
export const ProductFactSourceSchema = z.enum(PRODUCT_FACT_SOURCES);
export type ProductFactSource = z.infer<typeof ProductFactSourceSchema>;

// Curated concern evidence — a canonical slug backed by the sources that establish
// it. Never derived from catalog copy, ingredients, or reviews in this preview.
export const CONCERN_EVIDENCE_SOURCES = ['survey_mapping', 'brand_partner'] as const;
export const MatchedConcernSchema = z.object({
  concern: ProductConcernSchema,
  evidence: z.array(z.enum(CONCERN_EVIDENCE_SOURCES)).min(1),
});
export type MatchedConcern = z.infer<typeof MatchedConcernSchema>;

// --- Aggregates --------------------------------------------------------------

// Numerator + denominator beside a nullable percentage, so the agent can never
// present an unqualified percentage. Percentage is null when totalCount is zero.
export const BooleanAggregateSchema = z.object({
  trueCount: z.number().int(),
  totalCount: z.number().int(),
  percentage: z.number().nullable(),
});
export type BooleanAggregate = z.infer<typeof BooleanAggregateSchema>;

export const RatingAggregateSchema = z.object({
  ratingCount: z.number().int(),
  averageRating: z.number().nullable(),
  includeNextBox: BooleanAggregateSchema,
  notForMe: BooleanAggregateSchema,
  source: z.literal('member_aggregate'),
});
export type RatingAggregate = z.infer<typeof RatingAggregateSchema>;

export const PRODUCT_SEGMENT_TYPES = ['skin_type', 'age_range'] as const;
export const SegmentAggregateSchema = z.object({
  segmentType: z.enum(PRODUCT_SEGMENT_TYPES),
  segmentValue: z.string(),
  ratingCount: z.number().int(),
  averageRating: z.number(),
  source: z.literal('member_aggregate'),
});
export type SegmentAggregate = z.infer<typeof SegmentAggregateSchema>;

// Pooled requested skin-type segment used for skin-type-filtered ranking.
export const PooledSegmentSchema = z.object({
  skinTypes: z.array(ProductSkinTypeSchema),
  ratingCount: z.number().int(),
  averageRating: z.number(),
  source: z.literal('member_aggregate'),
});
export type PooledSegment = z.infer<typeof PooledSegmentSchema>;

// --- Deterministic INCI ------------------------------------------------------

export const INCI_PARSE_STATUSES = ['complete', 'review_needed', 'failed'] as const;
export const InciParseStatusSchema = z.enum(INCI_PARSE_STATUSES);
export type InciParseStatus = z.infer<typeof InciParseStatusSchema>;

// Zero-based, half-open UTF-16 offsets [start, end). The raw INCI string can be
// sliced directly with these offsets.
export const InciAliasSchema = z.object({
  name: z.string(),
  start: z.number().int(),
  end: z.number().int(),
});

export const ObservedIngredientSchema = z.object({
  observedName: z.string(),
  aliases: z.array(InciAliasSchema),
  position: z.number().int(),
  start: z.number().int(),
  end: z.number().int(),
  isTopFive: z.boolean(),
});
export type ObservedIngredient = z.infer<typeof ObservedIngredientSchema>;

export const InciDetailSchema = z.object({
  raw: z.string().nullable(),
  sourceHash: z.string().nullable(),
  parseStatus: InciParseStatusSchema,
  ingredients: z.array(ObservedIngredientSchema),
  // true only when a versioned exact marker appears in observed tokens; false
  // only on a complete parse with no marker; null for review_needed / failed / empty.
  listedFragranceMarker: z.boolean().nullable(),
  listedDryingAlcoholMarker: z.boolean().nullable(),
  markerPolicyVersion: z.string(),
  source: z.literal('deterministic_inci'),
});
export type InciDetail = z.infer<typeof InciDetailSchema>;

// --- find-products -----------------------------------------------------------

export const PRODUCT_SORTS = ['relevance', 'rating', 'review_count'] as const;
export const ProductSortSchema = z.enum(PRODUCT_SORTS);
export type ProductSort = z.infer<typeof ProductSortSchema>;

const findProductsInputBase = z
  .object({
    query: z.string().min(1).optional(),
    concerns: z.array(ProductConcernSchema).max(3).optional(),
    skin_types: z.array(ProductSkinTypeSchema).max(2).optional(),
    category: z.string().min(1).optional(),
    ingredients_include: z.array(z.string().min(1)).max(5).optional(),
    ingredients_exclude: z.array(z.string().min(1)).max(5).optional(),
    sort: ProductSortSchema.optional(),
    limit: z.number().int().min(1).max(10).default(5),
  })
  .strict();

// sort defaults conditionally: relevance when a query is present, rating otherwise.
// Explicit sort=relevance without a query is invalid and asks the model to retry.
export const FindProductsInputSchema = findProductsInputBase
  .superRefine((val, ctx) => {
    if (val.sort === 'relevance' && val.query === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['sort'],
        message: 'sort=relevance requires a query; omit sort or provide a query.',
      });
    }
  })
  .transform((val) => ({
    ...val,
    sort: val.sort ?? (val.query !== undefined ? 'relevance' : 'rating'),
  }));
export type FindProductsInput = z.infer<typeof FindProductsInputSchema>;

export const ProductCatalogSummarySchema = z.object({
  itemId: z.uuid(),
  title: z.string(),
  brand: z.string().nullable(),
  category: z.string().nullable(),
  source: z.literal('catalog'),
});

export const ProductRankingSchema = z.object({
  sort: ProductSortSchema,
  relevanceScore: z.number().nullable(),
  adjustedRating: z.number().nullable(),
  itemMean: z.number().nullable(),
  catalogMean: z.number().nullable(),
  n: z.number().int(),
});

export const MatchedSkinTypeSchema = z.object({
  skinType: ProductSkinTypeSchema,
  segment: SegmentAggregateSchema,
  brandPartnerTagged: z.boolean(),
});

export const INGREDIENT_MATCH_VIA = ['observed', 'alias'] as const;
export const IngredientFilterMatchSchema = z.object({
  parseStatus: InciParseStatusSchema,
  includeMatches: z.array(
    z.object({
      value: z.string(),
      observedName: z.string(),
      matchedVia: z.enum(INGREDIENT_MATCH_VIA),
    }),
  ),
  excludeVerifiedAbsent: z.array(z.string()),
});

export const ProductBrandPartnerSummarySchema = z.object({
  texture: z.string().nullable(),
  usageTime: z.string().nullable(),
  source: z.literal('brand_partner'),
});

export const ProductSummarySchema = z.object({
  catalog: ProductCatalogSummarySchema,
  overall: RatingAggregateSchema,
  requestedSegment: PooledSegmentSchema.nullable(),
  ranking: ProductRankingSchema,
  matchedConcerns: z.array(MatchedConcernSchema),
  matchedSkinTypes: z.array(MatchedSkinTypeSchema),
  ingredientFilter: IngredientFilterMatchSchema.nullable(),
  brandPartner: ProductBrandPartnerSummarySchema.nullable(),
});
export type ProductSummary = z.infer<typeof ProductSummarySchema>;

export const FindProductsResultSchema = z.object({
  products: z.array(ProductSummarySchema),
  dataVersion: ProductDataVersionSchema,
});
export type FindProductsResult = z.infer<typeof FindProductsResultSchema>;

// --- get-product -------------------------------------------------------------

export const GetProductInputSchema = z.object({ item_id: z.uuid() }).strict();
export type GetProductInput = z.infer<typeof GetProductInputSchema>;

export const ProductCatalogDetailSchema = z.object({
  itemId: z.uuid(),
  title: z.string(),
  brand: z.string().nullable(),
  category: z.string().nullable(),
  description: z.string().nullable(),
  shortDescription: z.string().nullable(),
  whyChosen: z.string().nullable(),
  directions: z.string().nullable(),
  source: z.literal('catalog'),
});

export const ProductBrandPartnerDetailSchema = z.object({
  texture: z.string().nullable(),
  usageTime: z.string().nullable(),
  fragrance: z.string().nullable(),
  skinTypes: z.array(z.string()),
  concerns: z.array(ProductConcernSchema),
  source: z.literal('brand_partner'),
});

export const ProductDetailSchema = z.object({
  catalog: ProductCatalogDetailSchema,
  overall: RatingAggregateSchema,
  segments: z.array(SegmentAggregateSchema),
  inci: InciDetailSchema,
  brandPartner: ProductBrandPartnerDetailSchema.nullable(),
  concerns: z.array(MatchedConcernSchema),
});
export type ProductDetail = z.infer<typeof ProductDetailSchema>;

export const GetProductResultSchema = z.object({
  product: ProductDetailSchema,
  dataVersion: ProductDataVersionSchema,
});
export type GetProductResult = z.infer<typeof GetProductResultSchema>;
